import { inject, Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Representative, RepresentativeMerchantAccess } from '@shared/models';
import {
  CreateRepresentativeRequest,
  RepresentativeService,
  UpdateRepresentativeRequest,
} from '@app/resources/services/representative.service';

@Injectable()
export class RepresentativesApiService {
  private readonly representativeService = inject(RepresentativeService);

  private readonly representativeChanged = new Subject<Representative>();
  readonly representativeChanged$ = this.representativeChanged.asObservable();

  /**
   * Feeds a representative pushed over SignalR (onRepresentativeChange) into
   * representativeChanged$ so views showing representative data can merge it in live.
   */
  applyRepresentativeChange(data: any): void {
    this.representativeChanged.next(mapToRepresentative(data));
  }

  getByCoalitionId(coalitionId: number): Observable<Representative[]> {
    return this.representativeService.getByCoalitionId(coalitionId).pipe(
      map((result: any) => {
        const values = result?.$values ?? [];
        return values.map(mapToRepresentative);
      })
    );
  }

  getById(representativeId: number): Observable<Representative | null> {
    return this.representativeService
      .getById(representativeId)
      .pipe(map((result: any) => (result ? mapToRepresentative(result) : null)));
  }

  create(createRepresentativeRequest: CreateRepresentativeRequest): Observable<Representative | null> {
    return this.representativeService
      .create(createRepresentativeRequest)
      .pipe(map((result: any) => (result ? mapToRepresentative(result) : null)));
  }

  update(
    representativeId: number,
    updateRepresentativeRequest: UpdateRepresentativeRequest
  ): Observable<Representative | null> {
    return this.representativeService
      .update(representativeId, updateRepresentativeRequest)
      .pipe(map((result: any) => (result ? mapToRepresentative(result) : null)));
  }

  getMerchantAccess(representativeId: number): Observable<RepresentativeMerchantAccess[]> {
    return this.representativeService.getMerchantAccess(representativeId).pipe(
      map((result: any) => {
        const values = result?.$values ?? [];
        return values.map(mapToRepresentativeMerchantAccess);
      })
    );
  }

  saveMerchantAccess(representativeId: number, merchantIds: number[]): Observable<boolean> {
    return this.representativeService.saveMerchantAccess(representativeId, merchantIds);
  }
}

function mapToRepresentative(data: any): Representative {
  return {
    RepresentativeId: data.representativeId,
    CoalitionId: data.coalitionId,
    NameFirst: data.nameFirst,
    NameLast: data.nameLast,
    EmailAddress: data.emailAddress,
    PhoneNumber: data.phoneNumber,
    AccessType: data.accessType ?? undefined,
    RepresentativeType: data.representativeType ?? undefined,
    LanguageType: data.languageType ?? undefined,
  };
}

function mapToRepresentativeMerchantAccess(data: any): RepresentativeMerchantAccess {
  return {
    MerchantAccessId: data.merchantAccessId,
    MerchantId: data.merchantId,
    MerchantName: data.merchantName,
  };
}
